'use client';
import Image from 'next/image';
import useLinks from '@/hooks/useLinks';
import Link from 'next/link';
import { SupportedLanguages, useTranslation } from '@/hooks/useTranslation';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaYoutube } from 'react-icons/fa6';
import { usePathname } from 'next/navigation';

type Props = { locale: string };

const FooterComponent = ({ locale }: Props) => {
  const pathname = usePathname();

  const { Links } = useLinks(locale as SupportedLanguages);
  const { t } = useTranslation(locale);

  const isChatPage = /\/[^\/]+\/chat\/[^\/]+/.test(pathname as string);

  if (isChatPage) {
    return null;
  }

  const icons = [
    {
      href: 'https://www.facebook.com/profile.php?id=61557329102949',
      label: 'Facebook',
      icon: <FaFacebookF />,
    },
    {
      href: 'https://www.youtube.com/@LouiseBot',
      label: 'YouTube',
      icon: <FaYoutube />,
    },
    {
      href: 'https://www.instagram.com/louisebot_chatbot',
      label: 'Instagram',
      icon: <FaInstagram />,
    },
    {
      href: 'https://www.linkedin.com/company/louisebot/',
      label: 'LinkedIn',
      icon: <FaLinkedinIn />,
    },
  ];

  return (
    <section id="footer-socialNetwork" className="tw-w-full tw-m-0 tw-p-0 tw-bg-[#fcfafb] tw-pt-16 lg:tw-pt-20">
      <div className="tw-container tw-mx-auto tw-px-4 tw-py-8 lg:tw-py-16">
        {/* Brand Section */}
        <div className="tw-mb-8 lg:tw-mb-12">
          <Link href={Links.home.href} className="tw-inline-flex tw-items-center tw-gap-2 tw-mb-4">
            <Image
              priority
              quality={100}
              className="tw-w-10 tw-h-10 tw-rounded-full"
              alt="LouiseBot | AI Chatbot for Business"
              crossOrigin="anonymous"
              src="https://cdn.1cdn.app/application/LOUISEBOT/25072015190193_logo_fond_blanc.png"
              width={32}
              height={32}
              title="LouiseBot logo"
            />
            <div className="tw-text-blue-600 tw-text-lg tw-font-extrabold tw-font-['Lexend'] tw-leading-7">
              LouiseBot
            </div>
          </Link>
          <p className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-max-w-md">
            {t('SiteSocialNetwork/text-01')}
          </p>
        </div>

        {/* Navigation Links Grid */}
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-6 lg:tw-gap-8 tw-mb-8 lg:tw-mb-12">
          {/* Company Section */}
          <div className="tw-col-span-1">
            <h3 className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-mb-3">
              {t('SiteNavbar/l-13')}
            </h3>
            <div className="tw-flex tw-flex-col tw-gap-2">
              <Link
                href={Links.contactUs.href}
                className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.contactUs.name}
              </Link>
              <Link
                href={Links.pricing.href}
                className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.pricing.name}
              </Link>
            </div>
          </div>

          {/* Products Section */}
          <div className="tw-col-span-1">
            <h3 className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-mb-3">
              {t('SiteNavbar/l-12')}
            </h3>
            <div className="tw-flex tw-flex-col tw-gap-2">
              <Link
                href={Links.AIChatbot.href}
                className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.AIChatbot.name}
              </Link>
              <Link
                href={Links.ScenarioChatbot.href}
                className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.ScenarioChatbot.name}
              </Link>
              <Link
                href={Links.VideoBot.href}
                className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.VideoBot.name}
              </Link>
              <Link
                href={Links.HumanClone.href}
                className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.HumanClone.name}
              </Link>
            </div>
          </div>

          {/* Resources Section */}
          <div className="tw-col-span-1">
            <h3 className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-mb-3">
              {t('SiteNavbar/l-11')}
            </h3>
            <div className="tw-flex tw-flex-col tw-gap-2">
              <Link
                href={Links.WhyVideoBot.href}
                className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.WhyVideoBot.name}
              </Link>
              <Link
                href={Links.TextVSVideo.href}
                className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.TextVSVideo.name}
              </Link>
              <Link
                href={Links.ChatbotIntegration.href}
                className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.ChatbotIntegration.name}
              </Link>
            </div>
          </div>

          {/* Partnership Section */}
          <div className="tw-col-span-1">
            <h3 className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-mb-3">
              {t('SiteNavbar/l-14')}
            </h3>
            <div className="tw-flex tw-flex-col tw-gap-2">
              <Link
                href={Links.BecomeAPartner.href}
                className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.BecomeAPartner.name}
              </Link>
              <Link
                href={Links.BecomeAnAffiliate.href}
                className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.BecomeAnAffiliate.name}
              </Link>
            </div>
          </div>
        </div>

        {/* Footer Bottom Section */}
        <div className="tw-border-t tw-border-gray-200 tw-pt-6">
          {/* Social Media & GDPR */}
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-center tw-gap-4 tw-mb-6">
            {/* Social Media Icons */}
            <div className="tw-flex tw-gap-3">
              {icons.map(({ href, label, icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="tw-text-white tw-bg-black tw-p-2 tw-rounded-full tw-transition-all tw-duration-200 hover:tw-bg-gray-800 hover:tw-scale-105"
                >
                  <span className="tw-text-sm">{icon}</span>
                </a>
              ))}
            </div>

            {/* GDPR Badge */}
            <div className="tw-flex tw-items-center">
              <Image
                priority
                quality={100}
                className="tw-w-10 tw-h-10"
                alt="GDPR Compliant"
                crossOrigin="anonymous"
                src="https://cdn.1cdn.app/application/LOUISEBOT/24120315525635_GDPR.png"
                width={320}
                height={320}
                title="GDPR Compliant"
              />
            </div>
          </div>

          {/* Legal Links & Copyright */}
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-center tw-gap-4 tw-text-sm">
            {/* Legal Links */}
            <div className="tw-flex tw-flex-wrap tw-justify-center sm:tw-justify-start tw-gap-4 lg:tw-gap-8">
              <Link
                href={Links.termsOfService.href}
                className="tw-text-gray-700 tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.termsOfService.name}
              </Link>
              <Link
                href={Links.faq.href}
                className="tw-text-gray-700 tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-hover:tw-text-blue-600 tw-transition-colors"
              >
                {Links.faq.name}
              </Link>
            </div>

            {/* Copyright */}
            <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-text-center sm:tw-text-right">
              © LouiseBot {new Date().getFullYear()} - {process.env.NEXT_PUBLIC_APP_VERSION as string}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FooterComponent;
